import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ViewLanguageService} from '../../app-services/view-language.service';
import {TimeZone} from '../../modelinterfaces/time-zone.model';
import {User} from '../../modelinterfaces/user.model';
import {UserService} from '../../services/user.service';
import { BaseUnsubscribeComponent} from "../../app-components/base-unsubscribe.component";
import {debounceTime, distinctUntilChanged, filter, finalize, takeUntil, tap} from "rxjs/operators";
import {forkJoin, Observable} from "rxjs";
import {AuthUserService} from "../../app-services/auth-user.service";
import {SelectLanguageComponent} from "../../app-components/select-language/select-language.component";
import {TranslateModule} from "@ngx-translate/core";
import {SelectTimezoneComponent} from "./select-timezone/select-timezone.component";
import {
  DialogActionsCancelSaveComponent
} from "../../dialog/dialog-save/dialog-actions-cancel-save/dialog-actions-cancel-save.component";

import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {
  STATE_UPDATE_FREQUENCY_MIN,
  UnitStateUpdateFrequencyLocalStorageService
} from "../../../../../user-portal/src/app/shared/services/local-storage-services/impl/unit-state-update-frequency.local-storage-service";
import {MaterialModule} from "@common-module/material.module";
import {DialogComponent} from "@common-module/dialog/dialog/dialog.component";

@Component({
    selector: 'app-settings-dialog',
  imports: [
    SelectLanguageComponent,
    TranslateModule,
    SelectTimezoneComponent,
    DialogActionsCancelSaveComponent,
    ReactiveFormsModule,
    MaterialModule,
    DialogComponent
],
    templateUrl: './settings-dialog.component.html',
    styleUrls: ['./settings-dialog.component.scss', '../../app-styles/dialog-common.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class SettingsDialogComponent extends BaseUnsubscribeComponent implements OnInit, OnDestroy {

  changeTracker: ChangeTracker;
  showSpinner = false;

  isAdminPortal = false;
  isDisabled: boolean = false;
  protected updateFrequency: FormControl<number>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { user: User, isDisabled?: boolean },
    private dialogRef: MatDialogRef<SettingsDialogComponent>,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private authUserService: AuthUserService,
    private viewLanguageService: ViewLanguageService,
    private readonly updateFrequencyLocalStorage: UnitStateUpdateFrequencyLocalStorageService) {
    super();
  }

  ngOnInit(): void {
    this.isAdminPortal = this.authUserService.isAdmin;
    const interfaceLanguage = this.viewLanguageService.get();
    this.changeTracker = new ChangeTracker(interfaceLanguage, this.data.user);
    if (this.data.isDisabled) {
      this.isDisabled = this.data.isDisabled;
    }
    if (!this.isAdminPortal) {
      this.createFrequencyForm();
    }
  }

  onInterfaceLanguageChange(lang: string) {
    this.changeTracker.interfaceLanguage = lang;
  }

  onAddressLanguageChange(lang: string) {
    this.changeTracker.addressLanguage = lang;
  }

  onTimezoneChange(tz: TimeZone) {
    this.changeTracker.timezone = tz;
  }

  public onSubmit(): void {
    if (!this.changeTracker.hasChanges) {
      this.closeDialog();
      return;
    }
    this.updateInterfaceLanguage();
    this.updateFrequencyStorage();
    this.updateAddressLanguageAndTimezone();
  }

  private updateInterfaceLanguage(): void {
    if (this.changeTracker.isInterfaceAddressChanged()) {
      this.viewLanguageService.set(this.changeTracker.interfaceLanguage);
    }
  }

  private updateFrequencyStorage(): void {
    if (this.changeTracker.isUpdateFrequencyChanged()) {
      this.updateFrequencyLocalStorage.save(this.changeTracker.updateFrequency);
    }
  }

  private updateAddressLanguageAndTimezone(): void {
    const tasks = this.createTasks();
    if (tasks.length > 0) {
      this.showSpinner = true;
      forkJoin(tasks)
        .pipe(takeUntil(this.destroy),
          finalize(() => {
            this.showSpinner = false;
            this.cdr.detectChanges();
            this.authUserService.update();
          }))
        .subscribe({
          complete: () => this.closeDialog()
        });
    } else {
      this.closeDialog();
    }
  }

  private createTasks(): Observable<any>[] {
    const tasks = [];
    if (this.changeTracker.isAddressLanguageChanged()) {
      const changeLanguageTask = this.userService.changeLanguageByName(this.data.user.id, this.changeTracker.addressLanguage);
      tasks.push(changeLanguageTask);
    }
    if (this.changeTracker.isTimezoneChanged()) {
      const changeTimezoneTask = this.userService.changeTimeZone(this.data.user.id, this.changeTracker.timezone);
      tasks.push(changeTimezoneTask);
    }
    return tasks;
  }

  private createFrequencyForm(): void {
    const savedFrequency: number = this.updateFrequencyLocalStorage.get();
    this.changeTracker.defaultUpdateFrequency = savedFrequency;
    this.updateFrequency = new FormControl<number>(savedFrequency, {
      updateOn: "change",
      validators: [Validators.required, Validators.min(STATE_UPDATE_FREQUENCY_MIN)]
    });
    this.updateFrequency.valueChanges
      .pipe(
        filter(value => this.updateFrequency.valid),
        debounceTime(500),
        distinctUntilChanged(),
        tap((value: number) => {
          this.changeTracker.updateFrequency = value;
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy)
      )
      .subscribe()
  }

  closeDialog(): void {
    if (this.changeTracker.isInterfaceAddressChanged() || this.changeTracker.isUpdateFrequencyChanged()) {
      window.location.reload();
    }
    this.dialogRef.close();
  }
}


class ChangeTracker {
  source: Data;
  changing: Data;

  hasChanges = false;

  constructor(interfaceLanguage: string,
              user: User) {
    this.source = new Data(interfaceLanguage, user.language.name, user.timeZone);
    this.changing = new Data(interfaceLanguage, user.language.name, user.timeZone);
  }

  get interfaceLanguage(): string {
    return this.changing.interfaceLanguage;
  }

  get addressLanguage(): string {
    return this.changing.addressLanguage;
  }

  get timezone(): TimeZone {
    return this.changing.timezone;
  }

  get updateFrequency(): number {
    return this.changing.updateFrequency;
  }

  set interfaceLanguage(lang: string) {
    this.changing.interfaceLanguage = lang;
    this.checkChanges();
  }

  set addressLanguage(lang: string) {
    this.changing.addressLanguage = lang;
    this.checkChanges();
  }

  set timezone(tz: TimeZone) {
    this.changing.timezone = tz;
    this.checkChanges();
  }

  set defaultUpdateFrequency(value: number) {
    this.source.updateFrequency = value;
  }

  set updateFrequency(value: number) {
    this.changing.updateFrequency = value;
    this.checkChanges();
  }

  isInterfaceAddressChanged(): boolean {
    return this.source.interfaceLanguage !== this.changing.interfaceLanguage;
  }

  isAddressLanguageChanged(): boolean {
    return this.source.addressLanguage !== this.changing.addressLanguage;
  }

  isTimezoneChanged(): boolean {
    return this.source.timezone.id !== this.changing.timezone.id;
  }

  isUpdateFrequencyChanged(): boolean {
    return this.source.updateFrequency &&
      this.changing.updateFrequency &&
      this.source.updateFrequency !== this.changing.updateFrequency;
  }

  private checkChanges() {
    this.hasChanges = this.isAddressLanguageChanged() ||
      this.isInterfaceAddressChanged() ||
      this.isTimezoneChanged() ||
      this.isUpdateFrequencyChanged();
  }
}

class Data {
  constructor(public interfaceLanguage: string,
              public addressLanguage: string,
              public timezone: TimeZone,
              public updateFrequency?: number) {
  }
}
